<template>
  <div id="chat-flows-list-mass" class="chat-flows-list-mass">
    <chat-flow-confirm-popup
      :isSidebarActive="isConfirmSidebarActive"
      :sourceTenant="sourceTenantDetails"
      :flowType="chatFlowType"
      :sourceOptionsCount="sourceOptionsCount"
      :includeSchedule="includeSchedule"
      :selectedTargets="selectedTargetTenantDetails"
      :activeUser="activeUserInfo"
      :isLoading="replicateLoading"
      @closeSidebar="toggleConfirmSidebar"
      @confirmReplication="replicateInMass"
    />

    <vs-card>
      <div>
        <vs-button
          color="primary"
          @click="toggleReplicationBuilder"
          :type="isReplicationBuilderVisible ? 'border' : 'filled'"
        >
          {{ isReplicationBuilderVisible ? 'Ocultar Configuração' : 'Configurar Replicação' }}
        </vs-button>
      </div>
    </vs-card>

    <vs-card v-if="isReplicationBuilderVisible" class="mt-4">
      <div class="header-section">
        <h4>Configuração da Replicação</h4>
        <small class="text-muted">Preencha as etapas abaixo para preparar a execução do fluxo.</small>
      </div>

      <div class="builder-stepper mt-4">
        <button type="button" class="builder-step" :class="builderStepClass(1)" @click="goToSetupStep(1)">
          <span class="builder-step__number">1</span>
          <span class="builder-step__content">
            <strong>Origem</strong>
            <small>Selecione o tenant de referência e carregue a visualização.</small>
          </span>
        </button>

        <button type="button" class="builder-step" :class="builderStepClass(2)" @click="goToSetupStep(2)">
          <span class="builder-step__number">2</span>
          <span class="builder-step__content">
            <strong>Destinos</strong>
            <small>Selecione as lojas que receberão o fluxo.</small>
          </span>
        </button>

        <button type="button" class="builder-step" :class="builderStepClass(3)" @click="goToSetupStep(3)">
          <span class="builder-step__number">3</span>
          <span class="builder-step__content">
            <strong>Revisão</strong>
            <small>Confira os dados antes da confirmação.</small>
          </span>
        </button>
      </div>

      <div v-if="replicationSetupStep === 1" class="builder-panel mt-4">
        <div class="builder-panel__header">
          <h5>Etapa 1. Definir origem</h5>
          <small class="text-muted">Selecione o tenant que será usado como referência e carregue o fluxo.</small>
        </div>

        <div class="grid-section mt-4">
          <div class="block">
            <label class="input-label">Tenant de origem (modelo)</label>
            <vs-select v-model="sourceTenantName" class="w-full" autocomplete>
              <vs-select-item
                v-for="tenant in sortedTenants"
                :key="tenantKey(tenant)"
                :value="tenantName(tenant)"
                :text="tenantDisplayName(tenant)"
              />
            </vs-select>

            <div class="mt-3 flex gap-2">
              <vs-button size="small" @click="loadSourceTemplate" :disabled="!sourceTenantName || templateLoading"> {{ templateLoading ? 'Carregando...' : 'Carregar Modelo' }} </vs-button>
              <vs-button size="small" type="border" color="dark" @click="resetTemplate" :disabled="templateLoading"> Limpar </vs-button>
            </div>

            <div v-if="chatFlowType === 'passive'" class="config-option mt-4">
              <div class="config-option__content">
                <strong>Replicar configurações do bot</strong>
                <small>Inclui Bot de Horário e demais configurações relacionadas ao bot passivo.</small>
              </div>

              <vs-switch v-model="includeSchedule" color="primary">
                <span slot="on">Sim</span>
                <span slot="off">Não</span>
              </vs-switch>
            </div>
          </div>

          <div class="block">
            <label class="input-label">Pré-visualização do fluxo</label>
            <div class="preview-box">
              <chat-flow-preview :treeData="previewTree" />
            </div>
            <small class="text-muted mt-2 block">
              Itens no modelo: {{ sourceOptionsCount }}
            </small>
          </div>
        </div>

        <div class="builder-actions mt-4">
          <vs-button size="small" color="primary" :disabled="!canAdvanceSourceStep" @click="goToSetupStep(2)">
            Avançar para destinos
          </vs-button>
        </div>
      </div>

      <div v-if="replicationSetupStep === 2" class="builder-panel mt-4">
        <div class="builder-panel__header">
          <h5>Etapa 2. Definir destinos</h5>
          <small class="text-muted">Escolha as lojas que receberão a replicação.</small>
        </div>

        <div class="filters mt-4">
          <vs-input v-model="tenantSearch" label="Buscar lojas" class="w-full md:w-1/2" />
        </div>

        <div class="actions mt-4">
          <vs-button size="small" type="border" @click="selectAllFiltered">Selecionar Filtradas</vs-button>
          <vs-button size="small" type="border" color="dark" @click="clearSelection">Limpar Seleção</vs-button>
          <span class="selection-counter">
            {{ selectedTargetTenants.length }} loja(s) selecionada(s)
          </span>
        </div>

        <div class="targets-table mt-4">
          <table class="w-full">
            <thead>
              <tr>
                <th class="checkbox-col">Selecionar</th>
                <th>Tenant</th>
                <th>CNPJ</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="tenant in filteredTargets" :key="`target-${tenantKey(tenant)}`">
                <td class="checkbox-col">
                  <input
                    type="checkbox"
                    class="target-checkbox"
                    :checked="isTargetSelected(tenantName(tenant))"
                    :disabled="tenantName(tenant) === sourceTenantName"
                    @change="toggleTarget(tenantName(tenant), $event.target.checked)"
                  />
                </td>
                <td>{{ tenantName(tenant) }}</td>
                <td>{{ tenant.cnpj || '-' }}</td>
              </tr>
              <tr v-if="!filteredTargets.length">
                <td colspan="4" class="empty-row">Nenhuma loja encontrada para os filtros atuais.</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="builder-actions mt-4">
          <vs-button size="small" type="border" color="dark" @click="goToSetupStep(1)">
            Voltar
          </vs-button>
          <vs-button size="small" color="primary" :disabled="!canAdvanceTargetStep" @click="goToSetupStep(3)">
            Avançar para revisão
          </vs-button>
        </div>
      </div>

      <div v-if="replicationSetupStep === 3" class="builder-panel mt-4">
        <div class="builder-panel__header">
          <h5>Etapa 3. Revisar dados</h5>
          <small class="text-muted">Confira os dados antes de abrir a confirmação final.</small>
        </div>

        <div class="review-grid mt-4">
          <div class="review-card">
            <small class="review-card__label">Fluxo</small>
            <p>{{ flowTypeLabel }}</p>
          </div>

          <div class="review-card">
            <small class="review-card__label">Origem</small>
            <p>{{ formatTenantDetail(sourceTenantDetails, sourceTenantName) }}</p>
          </div>

          <div class="review-card">
            <small class="review-card__label">Itens no modelo</small>
            <p>{{ sourceOptionsCount }}</p>
          </div>

          <div class="review-card">
            <small class="review-card__label">Configurações do bot</small>
            <p>{{ includeSchedule ? 'Replicar' : 'Não replicar' }}</p>
          </div>

          <div class="review-card">
            <small class="review-card__label">Destinos selecionados</small>
            <p>{{ selectedTargetTenants.length }}</p>
          </div>
        </div>

        <div class="review-list mt-4">
          <div class="review-list__header">
            <strong>Destinos selecionados</strong>
            <span>{{ selectedTargetTenants.length }} selecionada(s)</span>
          </div>

          <ul class="review-list__items">
            <li v-for="tenant in selectedTargetTenantDetails" :key="`review-${tenant.id || 'no-id'}-${tenant.name}`">
              <span>{{ tenant.name || '-' }}</span>
              <small>{{ tenant.id ? `ID ${tenant.id}` : 'Sem ID' }}<template v-if="tenant.cnpj"> | CNPJ {{ tenant.cnpj }}</template></small>
            </li>
          </ul>
        </div>

        <div class="builder-actions mt-4">
          <vs-button size="small" type="border" color="dark" @click="goToSetupStep(2)">
            Voltar
          </vs-button>
          <vs-button size="small" color="danger" :disabled="!canReplicate" @click="openConfirmSidebar">
            Confirmar replicação
          </vs-button>
        </div>
      </div>
    </vs-card>

    <div>
      <vs-table ref="logsTable" class="history-vs-table mt-4" :data="logs" :noDataText="logsNoDataText">
        <template slot="thead">
          <vs-th>Data</vs-th>
          <vs-th>IP</vs-th>
          <vs-th>Usuário</vs-th>
          <vs-th>Origem</vs-th>
          <vs-th>Status</vs-th>
          <vs-th>Dashs Afetadas</vs-th>
        </template>

        <template slot-scope="{data}">
          <tbody>
            <vs-tr :data="log" :key="`log-${log.id}`" v-for="log in data">
              <vs-td>
                <p class="history-cell">{{ formatDate(log.created_at) }}</p>
              </vs-td>

              <vs-td>
                <p class="history-cell">{{ log.ip_address || '-' }}</p>
              </vs-td>

              <vs-td>
                <p class="history-cell">{{ log.user ? log.user.name : '-' }}</p>
              </vs-td>

              <vs-td>
                <p class="history-cell">{{ formatTenantDetail(log.source_tenant_details, log.source_tenant) }}</p>
              </vs-td>

              <vs-td>
                <span class="status-tag" :class="`status-${log.status}`">{{ statusLabel(log.status) }}</span>
              </vs-td>

              <vs-td>
                <div class="history-affected" :class="{'history-affected--clickable': canExpandAffectedTenants(log)}">
                  <div class="history-affected__row">
                    <p class="history-cell" :class="isAffectedTenantsExpanded(log.id) ? 'history-cell--expanded' : 'history-cell--truncate'">
                      {{ affectedTenantsSummary(log) }}
                    </p>

                    <button
                      type="button"
                      class="history-affected__toggle"
                      v-if="canExpandAffectedTenants(log)"
                      @click.stop="toggleAffectedTenants(log)"
                    >
                      <vs-icon :icon="isAffectedTenantsExpanded(log.id) ? 'expand_less' : 'expand_more'" />
                    </button>
                  </div>

                  <div v-if="isAffectedTenantsExpanded(log.id)" class="history-states">
                    <div v-if="(log.affected_tenants || []).length" class="history-state history-state--success">
                      <strong>Concluídos ({{ (log.affected_tenants || []).length }})</strong>
                      <span>{{ (log.affected_tenants || []).join(', ') }}</span>
                    </div>

                    <div v-if="(log.processing_tenants || []).length" class="history-state history-state--processing">
                      <strong>Processando ({{ (log.processing_tenants || []).length }})</strong>
                      <span>{{ (log.processing_tenants || []).join(', ') }}</span>
                    </div>

                    <div v-if="(log.failed_tenants || []).length" class="history-state history-state--failed">
                      <strong>Falharam ({{ (log.failed_tenants || []).length }})</strong>
                      <ul>
                        <li v-for="entry in log.failed_tenants" :key="`fail-${log.id}-${entry.name}`">
                          <span>{{ entry.name }}</span>
                          <small v-if="entry.error">{{ entry.error }}</small>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </vs-td>
            </vs-tr>
          </tbody>
        </template>
      </vs-table>

      <div class="mt-4 flex justify-end">
        <vs-pagination
          :max="7"
          :total="pageCount"
          v-model="currentPage"
        />
      </div>
    </div>
  </div>
</template>

<script>
import {mapGetters} from 'vuex';
import {getIp} from '@/utils/index.js';
import ChatFlowPreview from './ChatFlowPreview.vue';
import ChatFlowConfirmPopup from "@/views/chat-flows/ChatFlowConfirmPopup.vue";

export default {
  name: 'chat-flows-list',
  components: {
    ChatFlowPreview,
    ChatFlowConfirmPopup
  },
  props: {
    type: {
      type: String,
      default: 'passive'
    }
  },
  data() {
    return {
      sourceTenantName: '',
      tenants: [],
      selectedTargetTenants: [],
      tenantSearch: '',
      includeSchedule: true,
      templateLoading: false,
      previewTree: [],
      sourceOptionsCount: 0,
      logs: [],
      logsLoading: false,
      logsPage: 1,
      logsTotalPages: 1,
      expandedAffectedLogIds: [],
      isReplicationBuilderVisible: false,
      replicationSetupStep: 1,
      isConfirmSidebarActive: false,
      replicateLoading: false,
      activeLogPolls: {},
    };
  },
  computed: {
    ...mapGetters(['token']),
    activeUserInfo() {
      return this.$store.state.AppActiveUser || {};
    },
    chatFlowType() {
      return this.type === 'active' ? 'active' : 'passive';
    },
    flowTypeLabel() {
      return this.chatFlowType === 'active' ? 'Bot de Campanhas' : 'Bot de Triagem';
    },
    sortedTenants() {
      return [...this.tenants].filter((tenant) => !tenant.deleted_at).sort((left, right) => {
        return this.tenantName(left).localeCompare(this.tenantName(right));
      });
    },
    filteredTargets() {
      return this.sortedTenants.filter(tenant => {
        const name = this.tenantName(tenant);
        const searchRef = `${name} ${tenant.cnpj || ''}`.toLowerCase();
        return this.tenantSearch.trim() === '' || searchRef.includes(this.tenantSearch.toLowerCase());
      });
    },
    canReplicate() {
      return (
        !this.replicateLoading &&
        !this.templateLoading &&
        !!this.sourceTenantName &&
        this.previewTree.length > 0 &&
        this.selectedTargetTenants.length > 0
      );
    },
    canAdvanceSourceStep() {
      return !!this.sourceTenantName && this.previewTree.length > 0 && !this.templateLoading;
    },
    canAdvanceTargetStep() {
      return this.canAdvanceSourceStep && this.selectedTargetTenants.length > 0;
    },
    logsNoDataText() {
      return this.logsLoading ? 'Carregando...' : 'Sem logs de auditoria para este tipo de fluxo.';
    },
    pageCount() {
      return this.logsTotalPages || 1;
    },
    sourceTenantDetails() {
      return this.buildTenantDetail(this.findTenantByName(this.sourceTenantName), this.sourceTenantName);
    },
    selectedTargetTenantDetails() {
      return this.selectedTargetTenants.map((tenantName) => {
        return this.buildTenantDetail(this.findTenantByName(tenantName), tenantName);
      });
    },
    currentPage: {
      get() {
        return this.logsPage;
      },
      set(value) {
        const parsed = Number(value);
        const nextPage = Number.isFinite(parsed) ? Math.trunc(parsed) : 1;
        const boundedPage = Math.min(Math.max(nextPage, 1), this.pageCount);

        if (boundedPage !== this.logsPage) {
          this.logsPage = boundedPage;
          this.fetchLogs();
        }
      }
    }
  },
  watch: {
    chatFlowType() {
      this.stopAllPolls();
      this.resetTemplate();
      this.logsPage = 1;
      this.replicationSetupStep = 1;
      this.isReplicationBuilderVisible = false;
      this.fetchLogs();
    },
    sourceTenantName() {
      this.selectedTargetTenants = this.selectedTargetTenants.filter(item => item !== this.sourceTenantName);
      this.resetTemplate();

      if (this.replicationSetupStep > 1) {
        this.replicationSetupStep = 1;
      }
    }
  },
  methods: {
    resetReplicationConfiguration() {
      this.sourceTenantName = '';
      this.selectedTargetTenants = [];
      this.tenantSearch = '';
      this.includeSchedule = true;
      this.previewTree = [];
      this.sourceOptionsCount = 0;
      this.replicationSetupStep = 1;
      this.isReplicationBuilderVisible = false;
      this.toggleConfirmSidebar(false);
    },
    toggleReplicationBuilder() {
      this.isReplicationBuilderVisible = !this.isReplicationBuilderVisible;

      if (!this.isReplicationBuilderVisible) {
        this.toggleConfirmSidebar(false);
      }

      if (this.isReplicationBuilderVisible && !this.canVisitSetupStep(this.replicationSetupStep)) {
        this.replicationSetupStep = 1;
      }
    },
    canVisitSetupStep(step) {
      if (step === 1) {
        return true;
      }

      if (step === 2) {
        return this.canAdvanceSourceStep;
      }

      if (step === 3) {
        return this.canAdvanceTargetStep;
      }

      return false;
    },
    builderStepClass(step) {
      return {
        'builder-step-active': this.replicationSetupStep === step,
        'builder-step-complete': this.replicationSetupStep > step,
        'builder-step-locked': !this.canVisitSetupStep(step)
      };
    },
    goToSetupStep(step) {
      if (!this.canVisitSetupStep(step)) {
        return;
      }

      this.replicationSetupStep = step;
    },
    findTenantByName(tenantName) {
      return this.tenants.find(item => this.tenantName(item) === tenantName) || null;
    },
    tenantName(tenant) {
      if (tenant && tenant.fqdn) { return tenant.fqdn.split('.')[0]; }
      return tenant && tenant.name ? tenant.name : '';
    },
    tenantDisplayName(tenant) {
      const name = this.tenantName(tenant);
      return tenant.cnpj ? `${name} (${tenant.cnpj})` : name;
    },
    tenantKey(tenant) {
      return `${this.tenantName(tenant)}-${tenant.website_id || tenant.id || 'no-id'}`;
    },
    buildTenantDetail(tenant, fallbackName = '') {
      const name = this.tenantName(tenant) || fallbackName || '-';

      return {
        id: tenant ? (tenant.website_id || tenant.id || null) : null,
        name,
        cnpj: tenant && tenant.cnpj ? tenant.cnpj : null,
      };
    },
    isTargetSelected(tenantName) {
      return this.selectedTargetTenants.includes(tenantName);
    },
    toggleTarget(tenantName, checked) {
      if (tenantName === this.sourceTenantName) {
        return;
      }

      if (checked && !this.selectedTargetTenants.includes(tenantName)) {
        this.selectedTargetTenants.push(tenantName);
        return;
      }

      this.selectedTargetTenants = this.selectedTargetTenants.filter(item => item !== tenantName);
    },
    selectAllFiltered() {
      const source = this.sourceTenantName;
      const selected = new Set(this.selectedTargetTenants);
      this.filteredTargets.forEach(tenant => {
        const tenantName = this.tenantName(tenant);
        if (tenantName && tenantName !== source) {
          selected.add(tenantName);
        }
      });
      this.selectedTargetTenants = Array.from(selected);
    },
    clearSelection() {
      this.selectedTargetTenants = [];

      if (this.replicationSetupStep > 2) {
        this.replicationSetupStep = 2;
      }
    },
    resetTemplate() {
      this.previewTree = [];
      this.sourceOptionsCount = 0;
      this.toggleConfirmSidebar(false);
    },
    toggleConfirmSidebar(val = false) {
      this.isConfirmSidebarActive = val;
    },
    async fetchTenants() {
      try {
        await this.$store.dispatch('getAllTenants', {
          token: this.token,
          filterUsers: true
        });

        this.tenants = this.$store.state.tenants.tenants || [];
      } catch (error) {
        console.error(error);
        this.tenants = [];
        this.$vs.notify({
          color: 'danger',
          title: 'Erro',
          text: 'Não foi possível carregar a lista de tenants.'
        });
      }
    },
    async loadSourceTemplate() {
      if (!this.sourceTenantName) {
        return;
      }

      this.templateLoading = true;
      try {
        const response = await this.$store.dispatch('getTenantChatFlowTemplate', {
          tenantName: this.sourceTenantName,
          flowType: this.chatFlowType,
          token: this.token
        });

        this.previewTree = response.data.preview_tree || [];
        this.sourceOptionsCount = response.data.chat_options_count || 0;
      } catch (error) {
        console.error(error);
        this.resetTemplate();
        this.$vs.notify({
          color: 'danger',
          title: 'Erro',
          text: 'Não foi possível carregar o fluxo do tenant de origem.'
        });
      } finally {
        this.templateLoading = false;
      }
    },
    async openConfirmSidebar() {
      if (!this.canReplicate) {
        return;
      }

      this.replicationSetupStep = 3;
      this.toggleConfirmSidebar(true);
    },
    async replicateInMass({authorizationPassword} = {}) {
      const password = authorizationPassword || '';
      if (password.trim() === '') {
        return;
      }

      this.replicateLoading = true;
      try {
        const clientIp = await getIp();
        if (!clientIp) {
          this.$vs.notify({
            color: 'danger',
            title: 'Erro',
            text: 'Não foi possível obter o IP da máquina para autorizar a auditoria da operação.'
          });
          return;
        }

        const response = await this.$store.dispatch('replicateTenantChatFlow', {
          sourceTenant: this.sourceTenantName,
          targetTenants: this.selectedTargetTenants,
          sourceTenantDetails: this.sourceTenantDetails,
          targetTenantDetails: this.selectedTargetTenantDetails,
          flowType: this.chatFlowType,
          includeSchedule: this.includeSchedule,
          authorizationPassword: password,
          clientIp,
          token: this.token
        });

        const data = response.data || {};
        const pending = data.pending_count || 0;

        if (pending > 0 && data.log_id) {
          this.$vs.notify({
            color: 'primary',
            title: 'Replicação iniciada',
            text: `${data.success_count || 0} concluído(s) | ${data.failed_count || 0} falha(s) | ${pending} em segundo plano. Acompanhe pelo histórico.`
          });
          this.resetReplicationConfiguration();
          await this.fetchLogs();
          this.startPollingLog(data.log_id);
        } else {
          this.$vs.notify({
            color: data.failed_count > 0 ? 'warning' : 'success',
            title: 'Replicação concluída',
            text: `Sucesso: ${data.success_count || 0} | Falha: ${data.failed_count || 0}`
          });
          this.resetReplicationConfiguration();
          await this.fetchLogs();
        }
      } catch (error) {
        console.error(error);
        this.$vs.notify({
          color: 'danger',
          title: 'Erro',
          text: this.extractErrorMessage(error, 'Não foi possível concluir a replicação em massa.')
        });
      } finally {
        this.replicateLoading = false;
      }
    },
    async fetchLogs() {
      this.logsLoading = true;
      try {
        const response = await this.$store.dispatch('getTenantChatFlowReplicationLogs', {
          flowType: this.chatFlowType,
          page: this.logsPage,
          limit: 10,
          token: this.token
        });

        const data = response.data || {};
        this.logs = data.items || [];
        this.logsTotalPages = data.total_pages || 1;
        this.expandedAffectedLogIds = [];

        (this.logs || []).forEach((log) => {
          if (log.status === 'processing') {
            this.startPollingLog(log.id);
          }
        });
      } catch (error) {
        console.error(error);
        this.logs = [];
        this.logsTotalPages = 1;
        this.expandedAffectedLogIds = [];
      } finally {
        this.logsLoading = false;
      }
    },
    startPollingLog(logId) {
      if (!logId || this.activeLogPolls[logId]) {
        return;
      }

      this.$store.dispatch('setShowLoadingOverlay', false);

      const startedAt = Date.now();
      const maxDurationMs = 10 * 60 * 1000;
      const intervalMs = 3000;

      const intervalId = setInterval(async () => {
        if (Date.now() - startedAt > maxDurationMs) {
          this.stopPollingLog(logId);
          return;
        }

        try {
          const response = await this.$store.dispatch('getTenantChatFlowReplicationLogById', {
            logId,
            token: this.token
          });

          const item = (response.data && response.data.items && response.data.items[0]) || null;
          if (!item) {
            this.stopPollingLog(logId);
            return;
          }

          const index = this.logs.findIndex((log) => log.id === logId);
          if (index >= 0) {
            this.$set(this.logs, index, item);
          }

          if (item.status !== 'processing') {
            this.stopPollingLog(logId);
            this.$vs.notify({
              color: item.failed_count > 0 ? 'warning' : 'success',
              title: 'Replicação finalizada',
              text: `Sucesso: ${item.success_count || 0} | Falha: ${item.failed_count || 0}`
            });
          }
        } catch (error) {
          console.error(error);
          this.stopPollingLog(logId);
        }
      }, intervalMs);

      this.$set(this.activeLogPolls, logId, intervalId);
    },
    stopPollingLog(logId) {
      const intervalId = this.activeLogPolls[logId];
      if (intervalId) {
        clearInterval(intervalId);
      }
      this.$delete(this.activeLogPolls, logId);

      if (!Object.keys(this.activeLogPolls).length) {
        this.$store.dispatch('setShowLoadingOverlay', true);
      }
    },
    stopAllPolls() {
      Object.keys(this.activeLogPolls).forEach((logId) => {
        this.stopPollingLog(Number(logId));
      });
    },
    formatDate(dateString) {
      if (!dateString) { return '-'; }
      const date = new Date(dateString);
      return date.toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },
    statusLabel(status) {
      if (status === 'success') {
        return 'Sucesso';
      }

      if (status === 'partial') {
        return 'Falha Parcial';
      }

      if (status === 'processing') {
        return 'Processando';
      }

      return 'Falhou';
    },
    formatTenantDetail(detail, fallbackName = '-') {
      if (detail && (detail.name || detail.id)) { return `${detail.name || fallbackName}`; }
      return fallbackName || '-';
    },
    formatTenantDetails(details = [], fallbackNames = []) {
      if (Array.isArray(details) && details.length > 0) { return details.map(item => this.formatTenantDetail(item, (item && item.name ? item.name : '-'))).join(', '); }
      return Array.isArray(fallbackNames) && fallbackNames.length > 0 ? fallbackNames.join(', ') : '-';
    },
    affectedTenantsSummary(log) {
      return this.formatTenantDetails(log.affected_tenant_details, log.affected_tenants);
    },
    canExpandAffectedTenants(log) {
      if (this.affectedTenantsSummary(log).length > 90) {
        return true;
      }

      const hasProcessing = (log.processing_tenants || []).length > 0;
      const hasFailed = (log.failed_tenants || []).length > 0;
      return hasProcessing || hasFailed;
    },
    isAffectedTenantsExpanded(logId) {
      return this.expandedAffectedLogIds.includes(logId);
    },
    toggleAffectedTenants(log) {
      if (!this.canExpandAffectedTenants(log)) { return; }
      if (this.isAffectedTenantsExpanded(log.id)) {
        this.expandedAffectedLogIds = this.expandedAffectedLogIds.filter(id => id !== log.id);
        return;
      }

      this.expandedAffectedLogIds.push(log.id);
    },
    extractErrorMessage(error, fallbackMessage) {
      const responseData = error && error.response ? error.response.data : null;

      if (typeof responseData === 'string' && responseData.trim() !== '') {
        return responseData;
      }

      if (responseData && responseData.message) {
        return responseData.message;
      }

      return fallbackMessage;
    }
  },
  async mounted() {
    await this.fetchTenants();
    await this.fetchLogs();
  },
  beforeDestroy() {
    this.stopAllPolls();
  }
}
</script>

<style lang="scss">
.chat-flows-list-mass {

  .page-hero__content {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .page-hero__eyebrow {
    display: inline-flex;
    width: fit-content;
    padding: 4px 10px;
    border-radius: 999px;
    background: #e0f2fe;
    color: #075985;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }

  .page-hero__actions {
    display: flex;
    justify-content: flex-end;
    align-items: center;
  }

  .header-section {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .builder-stepper {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 12px;
  }

  .builder-step {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    width: 100%;
    border: 1px solid #dbe3ef;
    border-radius: 12px;
    padding: 14px;
    background: #f8fafc;
    text-align: left;
    cursor: pointer;
    transition: border-color 0.2s ease, background 0.2s ease, transform 0.2s ease;
  }

  .builder-step:hover {
    border-color: #93c5fd;
    transform: translateY(-1px);
  }

  .builder-step__number {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 32px;
    height: 32px;
    border-radius: 999px;
    background: #dbeafe;
    color: #1d4ed8;
    font-size: 13px;
    font-weight: 700;
  }

  .builder-step__content {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .builder-step__content strong {
    color: #111827;
    font-size: 13px;
  }

  .builder-step__content small {
    color: #6b7280;
    font-size: 12px;
    line-height: 1.45;
  }

  .builder-step-active {
    border-color: #2563eb;
    background: #eff6ff;
  }

  .builder-step-complete {
    border-color: #86efac;
    background: #f0fdf4;
  }

  .builder-step-complete .builder-step__number {
    background: #dcfce7;
    color: #15803d;
  }

  .builder-step-locked {
    opacity: 0.55;
  }

  .builder-step-locked:hover {
    border-color: #dbe3ef;
    transform: none;
  }

  .builder-panel {
    border: 1px solid #e5e7eb;
    border-radius: 14px;
    padding: 18px;
    background: #ffffff;
  }

  .builder-panel__header {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .builder-panel__header h5 {
    margin: 0;
    color: #111827;
    font-size: 16px;
  }

  .grid-section {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
  }

  .block {
    min-width: 0;
  }

  .input-label {
    display: block;
    margin-bottom: 6px;
    font-size: 12px;
    color: #6b7280;
  }

  .preview-box {
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    padding: 10px;
    min-height: 160px;
    background: #f9fafb;
  }

  .config-option {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
    border: 1px solid #e5e7eb;
    border-radius: 10px;
    background: #ffffff;
    padding: 12px;
  }

  .config-option__content {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .config-option__content strong {
    color: #111827;
    font-size: 13px;
  }

  .config-option__content small {
    color: #6b7280;
    font-size: 12px;
    line-height: 1.45;
  }

  .filters {
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
  }

  .actions {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }

  .builder-actions {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    flex-wrap: wrap;
  }

  .selection-counter {
    color: #374151;
    font-size: 12px;
    margin-left: auto;
  }

  .targets-table {
    overflow-x: auto;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    max-height: 360px;
    overflow-y: auto;
  }

  .targets-table table {
    border-collapse: collapse;
    min-width: 100%;
  }

  .targets-table th,
  .targets-table td {
    text-align: left;
    padding: 10px;
    border-bottom: 1px solid #e5e7eb;
    font-size: 12px;
  }

  .targets-table th {
    background: #f3f4f6;
    position: sticky;
    top: 0;
    z-index: 1;
  }

  .checkbox-col {
    width: 100px;
  }

  .target-checkbox {
    width: 14px;
    height: 14px;
    cursor: pointer;
  }

  .empty-row {
    text-align: center;
    color: #6b7280;
  }

  .review-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;
  }

  .review-card {
    border: 1px solid #e5e7eb;
    border-radius: 10px;
    background: #ffffff;
    padding: 12px;
  }

  .review-card__label {
    color: #6b7280;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  .review-card p {
    margin: 6px 0 0;
    color: #111827;
    font-size: 14px;
    font-weight: 600;
  }

  .review-list {
    border: 1px solid #e5e7eb;
    border-radius: 10px;
    background: #ffffff;
    padding: 12px;
  }

  .review-list__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    margin-bottom: 10px;
    font-size: 12px;
    color: #111827;
  }

  .review-list__items {
    list-style: none;
    margin: 0;
    padding: 0;
    max-height: 220px;
    overflow-y: auto;
  }

  .review-list__items li {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    padding: 10px 0;
    border-bottom: 1px solid #e5e7eb;
    font-size: 13px;
    color: #111827;
  }

  .review-list__items li:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }

  .review-list__items small {
    color: #6b7280;
    text-align: right;
  }

  .vs-con-table {
    .product-name {
      max-width: 23rem;
    }

    .vs-table--header {
      display: flex;
      flex-wrap: wrap-reverse;
      margin-left: 1.5rem;
      margin-right: 1.5rem;

      >span {
        display: flex;
        flex-grow: 1;
      }

      .vs-table--search {
        padding-top: 0;

        .vs-table--search-input {
          padding: 0.9rem 2.5rem;
          font-size: 1rem;

          &+i {
            left: 1rem;
          }

          &:focus+i {
            left: 1rem;
          }
        }
      }
    }

    .vs-table {
      border-collapse: separate;
      border-spacing: 0 1.3rem;
      padding: 0 1rem;

      tr {
        box-shadow: 0 4px 20px 0 rgba(0, 0, 0, .05);

        td {
          padding: 10px;
          text-align: center;
          &:first-child {
            border-top-left-radius: .5rem;
            border-bottom-left-radius: .5rem;
          }

          &:last-child {
            border-top-right-radius: .5rem;
            border-bottom-right-radius: .5rem;
          }

          &.img-container {
            span {
              display: flex;
              justify-content: flex-start;
            }

            .product-img {
              height: 110px;
            }
          }
        }

        td.td-check {
          padding: 20px !important;
        }
        td.td.vs-table--td.actionTD > span {
          display: flex;
          justify-content: center;
        }
      }
    }

    .vs-table--thead {
      th {
        padding-top: 0;
        padding-bottom: 0;

        .vs-table-text {
          text-transform: uppercase;
          font-weight: 600;
          justify-content: center !important;
        }
      }

      th.td-check {
        padding: 0 15px !important;
      }

      tr {
        background: none;
        box-shadow: none;
      }
    }

    .vs-table--pagination {
      justify-content: center;
    }
  }

  .history-cell {
    margin: 0;
    font-size: 12px;
    font-weight: 500;
    color: #111827;
    line-height: 1.5;
  }

  .history-affected {
    width: 100%;
  }

  .history-affected--clickable {
    cursor: default;
  }

  .history-affected__row {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    width: 100%;
  }

  .history-cell--truncate {
    display: block;
    max-width: 360px;
    width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .history-cell--expanded {
    max-width: 360px;
    width: 100%;
    white-space: normal;
    word-break: break-word;
  }

  .history-affected__toggle {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 auto;
    width: 24px;
    height: 24px;
    border: 0;
    background: transparent;
    color: #2563eb;
    cursor: pointer;
    padding: 0;
  }

  .history-affected__toggle:focus {
    outline: none;
  }

  .history-affected__toggle i {
    font-size: 18px !important;
  }

  .status-tag {
    display: inline-block;
    border-radius: 999px;
    padding: 2px 8px;
    font-size: 11px;
    text-transform: uppercase;
    font-weight: 600;
  }

  .status-success {
    color: #065f46;
    background: #d1fae5;
  }

  .status-failed {
    color: #991b1b;
    background: #fee2e2;
  }

  .status-partial {
    color: #92400e;
    background: #fef3c7;
  }

  .status-processing {
    color: #1d4ed8;
    background: #dbeafe;
  }

  .history-states {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-top: 8px;
    padding: 8px 10px;
    border-radius: 8px;
    background: #f8fafc;
  }

  .history-state {
    display: flex;
    flex-direction: column;
    gap: 2px;
    font-size: 12px;
  }

  .history-state strong {
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  .history-state--success strong { color: #065f46; }
  .history-state--processing strong { color: #1d4ed8; }
  .history-state--failed strong { color: #991b1b; }

  .history-state ul {
    margin: 4px 0 0 0;
    padding-left: 16px;
  }

  .history-state ul li {
    display: flex;
    flex-direction: column;
  }

  .history-state ul li small {
    color: #6b7280;
    font-size: 11px;
  }

  @media (max-width: 920px) {
    .page-hero,
    .review-list__header,
    .review-list__items li {
      flex-direction: column;
      align-items: flex-start;
    }

    .builder-stepper,
    .grid-section {
      grid-template-columns: 1fr;
    }

    .config-option {
      flex-direction: column;
      align-items: flex-start;
    }

    .review-grid {
      grid-template-columns: 1fr;
    }

    .selection-counter {
      margin-left: 0;
      width: 100%;
    }

    .page-hero__actions,
    .builder-actions {
      width: 100%;
      justify-content: flex-start;
    }
  }
}
</style>
